import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { AdminSprayRecord } from '../data-models/admin-spray-record.model';
import { AdminSprayRecordSummary } from '../response-models/admin-spray-record-summary.model';
import { ApiListResponse } from '../response-models/api-list-response.model';
import { Page } from '../response-models/page.model';
import { toAdminSprayRecordSummary } from '../utils/admin-spray-record-wire-utils';
import { listOf } from '../utils/wire-value-utils';

/** Filters and page for `GET /admin/spray-records`. Blank values are not sent. */
export interface SprayRecordListQuery {
  page?: number;
  pageSize?: number;
  organizationId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Spray records across all organizations, read-only: `/admin/spray-records[/:id]` (hub
 * `admin-spray-records-controller.ts`), paged with `pageSize`. The list returns summary rows; a single record
 * (`AdminSprayRecord`) carries its chemicals, drone flights, field and customer.
 */
@Injectable({ providedIn: 'root' })
export class SprayRecordService extends BaseService<AdminSprayRecord> {
  constructor() {
    super('admin/spray-records', AdminSprayRecord);
  }

  /** GET /admin/spray-records: one page of summary rows, newest application date first. */
  getRecords(query: SprayRecordListQuery = {}): Observable<Page<AdminSprayRecordSummary>> {
    // The rows are summaries, not records, so they are read here rather than through `getPage`.
    return this.requestEnvelope('GET', { query: toListQuery(query) }, toSummaryPage);
  }

  /** GET /admin/spray-records/:id. Emits `null` when the hub returns no data. */
  getRecord(recordId: string): Observable<AdminSprayRecord | null> {
    return this.get(recordId);
  }
}

function toListQuery(query: SprayRecordListQuery): Record<string, string | number | undefined> {
  return {
    page: query.page,
    pageSize: query.pageSize,
    organizationId: query.organizationId || undefined,
    status: query.status || undefined,
    startDate: query.startDate || undefined,
    endDate: query.endDate || undefined,
  };
}

function toSummaryPage(body: ApiListResponse<unknown> | null): Page<AdminSprayRecordSummary> {
  const items = listOf(body?.data, toAdminSprayRecordSummary);
  const paging = body?.paging ?? { page: 1, pageSize: items.length, totalItems: items.length, totalPages: 1 };
  return { items, paging };
}
